"use client";

import { useState, useEffect, ReactElement, useRef } from "react";
import { useSearchParams } from "next/navigation";

import { THelpTopic } from "../../actions/server/helpSection";
import HelpSectionStage from "./HelpSectionStage/HelpSectionStage";

type THelpSectionProps = {
  topic: THelpTopic;
  getResponse: (resultId: string) => Promise<ReactElement | ReactElement[]>;
};

type Category = {
  parentId?: string;
  selected?: boolean;
  result?: string;
};

export type TNestedStep = THelpTopic[string];

export type TLevelProps = {
  selectedCategory: TNestedStep;
  stepNumber: number;
};

export const findAllCategories = (
  selectedCategory: string,
  categories: THelpTopic,
  currentPath = {} as THelpTopic
): THelpTopic | null => {
  for (const category in categories) {
    const thisCategory = categories[category];

    if (currentPath) {
      currentPath[thisCategory.id] = thisCategory;
    }

    if (thisCategory.id === selectedCategory) {
      return currentPath;
    }

    if (
      thisCategory.categories &&
      Object.keys(thisCategory.categories).length > 0
    ) {
      const result = findAllCategories(
        selectedCategory,
        thisCategory.categories,
        currentPath
      );
      if (result) return result;
    }
  }
  return null;
};

export const findSelectedCategories = (
  selectedCategory: string,
  categories: Record<string, Category> | null
) => {
  if (!categories) return null;

  const selectedKeys: string[] = [];

  let currentCategory = selectedCategory;

  while (currentCategory) {
    selectedKeys.unshift(currentCategory);

    const parentId = categories[currentCategory]?.parentId;

    if (!parentId) break;

    currentCategory = parentId;
  }

  const selectedCategories = Object.fromEntries(
    Object.entries(categories).filter(([key]) => selectedKeys.includes(key))
  );

  return selectedCategories;
};

export default function HelpSectionCC({
  topic,
  getResponse,
}: THelpSectionProps) {
  const [stepperData, setStepperData] = useState<THelpTopic>(topic);
  const [response, setResponse] = useState<
    ReactElement | ReactElement[] | null
  >(null);
  const [responseScroll, setResponseScroll] = useState<boolean>(false);
  const responseScrollRef = useRef(null);
  const searchParams = useSearchParams();

  const getNextLevel = async ({
    selectedCategory,
    stepNumber,
  }: TLevelProps) => {
    // clear the response
    setResponse(null);

    // trim the object to required number of steps
    const updatedSteps = Object.keys(stepperData)
      .filter((step, i) => {
        return i <= stepNumber;
      })
      .reduce((acc: THelpTopic, step: string) => {
        acc[step] = stepperData[step];
        return acc;
      }, {});

    // clear out selected states
    if (selectedCategory.categories) {
      for (const category in selectedCategory.categories) {
        selectedCategory.categories[category].selected = false;
      }
    }

    // add the new one to the render object
    updatedSteps[selectedCategory.id] = selectedCategory;

    // set the right category as selected in the parent
    if (selectedCategory.parentId) {
      for (const category in updatedSteps[selectedCategory.parentId]
        .categories) {
        const thisCategory =
          updatedSteps[selectedCategory.parentId].categories[category];
        thisCategory.selected = thisCategory.id === selectedCategory.id;
      }
    }

    setStepperData(updatedSteps);

    // fetch/set the rendered result if present
    if (selectedCategory.result) {
      const answer = await getResponse(selectedCategory.result);
      setResponse(answer);
    }
  };

  useEffect(() => {
    const loadCategory = async () => {
      if (searchParams) {
        const onLoadCategory = searchParams.get("category");
        if (!onLoadCategory) return;

        const getOnLoadData = async (resultId: string) => {
          const answer = await getResponse(resultId);
          setResponse(answer);
        };

        if (onLoadCategory) {
          const allCategories = findAllCategories(onLoadCategory, topic);
          const categoriesSelected = allCategories
            ? findSelectedCategories(onLoadCategory, allCategories)
            : null;

          for (const category in categoriesSelected) {
            const thisCategory = categoriesSelected[category];
            thisCategory.selected = true;

            if (thisCategory.result) {
              getOnLoadData(thisCategory.result);
            }
          }
          if (categoriesSelected) {
            setStepperData(categoriesSelected as THelpTopic);
            setResponseScroll(true);
          }
        }
      }
    };

    loadCategory();
  }, []);

  useEffect(() => {
    if (responseScroll) {
      const timeout = setTimeout(() => {
        const element = document.getElementById("scrollTarget");
        if (!element) return;
        const SCROLL_OFFSET = -32;
        const top =
          element.getBoundingClientRect().top + window.scrollY + SCROLL_OFFSET;
        window.scrollTo({ top: top });
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [responseScroll]);

  return (
    <>
      {Object.keys(stepperData).map((step, stepNumber) => (
        <HelpSectionStage
          ref={
            stepNumber === Object.keys(stepperData).length - 1
              ? responseScrollRef
              : null
          }
          stepNumber={stepNumber}
          {...stepperData[step]}
          key={`step_${stepNumber}`}
          callback={getNextLevel}
          currentStep={Object.keys(stepperData).length}
          response={stepperData[step].result ? response ?? null : null}
          isResponse={!!stepperData[step].result}
        />
      ))}
    </>
  );
}
